<script setup>

const runtimeConfig = useRuntimeConfig()

const error = defineProps({
  error: {
    type: Object,
    required: true
  }
})

const houseLogo = ref('')
const isDev = process.env.NODE_ENV === 'development'

// Refs para DOM
const containerRef = ref(null)
const cardRef = ref(null)
const buttonRef = ref(null)

let houseBackground = ''
let houseCard = ''
let housePrimary = ''

// Verifica URL e define estilos
if (runtimeConfig.public.API_URL === 'https://app.br4bet.com') {
  houseLogo.value = 'logoBr4bet'
  houseBackground = 'br4bet-bg'
  houseCard = 'br4bet-card'
  housePrimary = 'br4bet-primary'
} else if (runtimeConfig.public.API_URL === 'https://app.metgol.io') {
  houseLogo.value = 'logoMetgol'
  houseBackground = 'metgol-bg'
  houseCard = 'metgol-card'
  housePrimary = 'metgol-primary'
} else if (runtimeConfig.public.API_URL === 'https://app.goldebet.com') {
  houseLogo.value = 'logoGoldebet'
  houseBackground = 'goldebet-bg'
  houseCard = 'goldebet-card'
  housePrimary = 'goldebet-primary'
} else if (runtimeConfig.public.API_URL === 'https://backoffice.lotogreen.com') {
  houseLogo.value = 'logoLotogreen'
  houseBackground = 'lotogreen-bg'
  houseCard = 'lotogreen-card'
  housePrimary = 'lotogreen-primary'
} else if (runtimeConfig.public.API_URL === 'https://app.met-bet.io') {
  houseLogo.value = 'logoMet-bet'
  houseBackground = 'met-bet-bg'
  houseCard = 'met-bet-card'
  housePrimary = 'met-bet-primary'
} else {
  houseLogo.value = 'error'
  houseBackground = 'default-bg'
  houseCard = 'default-card'
  housePrimary = 'default-primary'
}

onMounted(async () => {
  await nextTick() // garante que o DOM esteja totalmente montado com as classes do template
  containerRef.value?.classList.add(houseBackground)
  cardRef.value?.classList.add(houseCard)
  buttonRef.value?.classList.add(housePrimary)
})

const redirectToHome = () => {
  const cacheValue = Date.now()
  window.location.href = `/?cache=${cacheValue}`
}
</script>

<template>
    <div class="" ref="containerRef">
      <div class="content">
        <div class="box">
          <img class="logo" :src="`/logos/${houseLogo}.png`" />
          <h2 class="h2" v-if="error.error.statusCode === 404">Página Não Encontrada.</h2>
          <h2 class="h2" v-else>Voltamos em breve!</h2>

          <div v-if="!isDev" class="message" ref="cardRef">
            <p v-if="error.error.statusCode === 404">
              A página que você procura pode ter sido removida, renomeada ou nunca existiu.
              <br /><br />
              Agradecemos pela compreensão!<br /> Por favor, verifique o endereço ou volte para a página inicial.
            </p>
            <p v-else>
              Ops! Algo deu errado. Tente novamente mais tarde ou entre em contato com o suporte.
              <br /><br />
              Obrigado pela paciência!<br />
              Em instantes, tudo estará de volta ao normal.
            </p>
          </div>

          <div class="message erro" v-if="isDev">
            <p class="font-bold text-4xl text-red-500">{{ error.error.statusCode }}</p>
            <p class="text-green-600 text-sm">{{ error.error.stack }}</p>
          </div>

          <button class="button" ref="buttonRef" @click="redirectToHome">
            <p v-if="error.error.statusCode === 404">Página Inicial</p>
            <p v-else>Recarregar</p>
            <Icon :name="error.error.statusCode === 404 ? 'mingcute:external-link-line' : 'material-symbols:sync-outline'" />
          </button>
        </div>
      </div>
    </div>
</template>

<style>
.content {
  display: flex;
  height: 100vh;
  width: 100vw;
  justify-content: center;
  text-align: center;
  color: white;
  overflow-y: auto;
  padding-bottom: 16px;
}
.box {
  max-width: 32rem;
  margin: auto 0;
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
}
.logo {
  border-radius: 8px;
  width: 13rem;
  margin-bottom: 20px;
}
.message {
  margin-top: 20px;
  border-radius: 8px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  padding: 16px;
}
.button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid gray;
  padding: 12px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  margin: 20px 0;
}
.h2 {
  font: bold;
  font-size: 25px;
}

.erro { 
  max-width: 100vw;
  padding: 10px;
  white-space: normal; /* quebra linha automaticamente */
  word-wrap: break-word; /* força quebra de palavras grandes */
}

/* Definições de cores personalizadas */
.br4bet-bg { background-color: #11181F; }
.br4bet-card { background-color: #1A252C; }
.br4bet-primary:hover { border-color: #09B206; }

.metgol-bg { background-color: #091322; }
.metgol-card { background-color: #243A5A; }
.metgol-primary:hover { border-color: #CA312B; }

.goldebet-bg { background-color: #212425; }
.goldebet-card { background-color: #323637; }
.goldebet-primary:hover { border-color: #FAB701; }

.lotogreen-bg { background-color: #001610; }
.lotogreen-card { background-color: #024B39; }
.lotogreen-primary:hover { border-color: #1CBF24; }

.met-bet-bg { background-color: #0F1D24; }
.met-bet-card { background-color: #14252E; }
.met-bet-primary:hover { border-color: #FE5D54; }

.default-bg { background-color: #192333; }
.default-card { background-color: #334155; }
.default-primary:hover { border-color: #22C55E; }
</style>
